/**
 * `incrementFollowersCount` — owns the `useGetFollowsCounts` row patch on
 * a `UserNotification` event of kind=FOLLOW_RECEIVED.
 *
 * Context: `useGetFollowsCounts` caches `{followers, following}` for the
 * viewer. When someone else follows the viewer, the backend-api publishes
 * a `morfi.user.${userId}.notifications.v0` event (kind=FOLLOW_RECEIVED).
 * The notification payload carries no numeric delta — the "amount" is
 * always +1 — so the realtime patch needs a tiny helper instead of a
 * payload-field reference. The helper bumps `followers` by 1.
 *
 * Recipe shape:
 *   set: { followers: kind, helper: incrementFollowersCount }
 *
 * The LHS/RHS pair is documentation only — the helper owns the line.
 * Codegen emits ONLY `HELPERS.incrementFollowersCount(row, raw)`.
 *
 * Bails to the exact `prev` reference when `followers` is missing —
 * keeps the structural-sharing short-circuit intact.
 *
 * De-dupe: the user who PERFORMED the follow already applied an optimistic
 * `followers + 1` in `applyFollowOptimistic`. They are also subscribed to X's
 * notifications channel via `useGetFollowsCounts(X)`, so without a guard the
 * realtime echo would double-count on their screen. We can't match by actor id
 * (it resolves to `null` in demo mode), so `applyFollowOptimistic` records the
 * follow and we swallow that one echo here — keyed by `args.userId` (= X). A
 * bystander on X's profile, or X viewing their own profile, has no such mark,
 * so they still get the realtime +1.
 */

import { consumeOptimisticFollowEcho } from './follow-echo-dedup.js';

interface FollowsCountsRow {
  readonly followers?: number;
  readonly following?: number;
}

export function incrementFollowersCount<T extends FollowsCountsRow>(
  prev: T,
  _raw: Record<string, unknown>,
  args?: Record<string, unknown>,
): T {
  if (typeof prev.followers !== 'number') return prev;
  const x = typeof args?.userId === 'string' ? args.userId : null;
  if (x !== null && consumeOptimisticFollowEcho(x)) return prev;
  return { ...prev, followers: prev.followers + 1 };
}
